import { useGetAllAccounts } from '@/api/generated/accounts/accounts';
import { useGetPlanRetirement } from '@/api/generated/flow/flow';
import { useGetAllGoals } from '@/api/generated/goals/goals';
import { useGetProfile } from '@/api/generated/settings/settings';
import { useGetAllTransactions } from '@/api/generated/transactions/transactions';
import { ProjectedNetWorthChart } from '@/components/planning/charts/ProjectedNetWorthChart';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  buildNetWorthTimeline,
  horizonToTimePeriod,
  parseBirthYear,
  usePlansList,
} from '@/features/planning';
import { useLanguage } from '@/i18n/language-hooks';
import { cn, formatCurrency, useDefaultCurrency } from '@/lib/utils';
import { ArrowUpRight } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

export function PlanningOverviewWidget() {
  const { t } = useLanguage();
  const currency = useDefaultCurrency();
  const { plans, isLoading: plansLoading } = usePlansList();
  const plan = plans.find((p) => p.isDefault) ?? plans[0];
  const planId = plan?.id;

  const { data: accountsResponse } = useGetAllAccounts();
  const accounts = useMemo(() => accountsResponse?.items ?? [], [accountsResponse]);
  const { data: transactionsResponse } = useGetAllTransactions();
  const transactions = useMemo(() => transactionsResponse?.items ?? [], [transactionsResponse]);
  const { data: goalsResponse } = useGetAllGoals();
  const goals = useMemo(() => goalsResponse?.items ?? [], [goalsResponse]);
  const { data: retirement } = useGetPlanRetirement(planId ?? '', {
    query: { enabled: Boolean(planId) },
  });
  const { data: profile } = useGetProfile();
  const birthYear = parseBirthYear(profile?.personalInfo?.dateOfBirth);

  const timeline = useMemo(() => {
    if (!plan || !retirement) return null;
    const period = horizonToTimePeriod(plan.horizon);
    return buildNetWorthTimeline({
      accounts,
      transactions,
      assumptions: plan.assumptions,
      goals,
      retirement,
      rangeYears: period.rangeYears,
      birthYear,
      pastMonths: 12,
    });
  }, [accounts, birthYear, goals, plan, retirement, transactions]);

  if (plansLoading) {
    return <Skeleton className='h-48 w-full' />;
  }

  if (!timeline) return null;

  return (
    <Card>
      <CardHeader className='flex flex-row items-center justify-between space-y-0'>
        <CardTitle className='text-base'>{t('planning.widget.title')}</CardTitle>
        <Button variant='ghost' size='icon' className='size-7 shrink-0' asChild>
          <Link
            to={`/planning/${planId}/overview`}
            aria-label={t('planning.widget.open')}
            title={t('planning.widget.open')}
            className={cn(
              'text-muted-foreground hover:bg-accent hover:text-accent-foreground inline-flex items-center justify-center rounded-md transition-colors'
            )}
          >
            <ArrowUpRight className='size-4' aria-hidden />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        <p className='text-fin-gray-400 mb-2 text-sm'>
          {t('planning.widget.projected', {
            amount: formatCurrency(timeline.projectedEndValue, currency),
          })}
        </p>
        <ProjectedNetWorthChart
          timeline={timeline}
          goals={goals}
          birthYear={birthYear}
          retirementAge={retirement?.retirementAge}
        />
      </CardContent>
    </Card>
  );
}
